import type { AdminSession } from "@/lib/session";
import { hasPermission } from "@/lib/session";
import type { AdminModule, ModuleRoute, NavGroup } from "./types";
import { NAV_GROUPS } from "./types";
import { overviewModule } from "./overview/module";
import { usersModule } from "./users/module";
import { accountsModule } from "./accounts/module";
import { signupsModule } from "./signups/module";
import { revenueModule } from "./revenue/module";
import { paymentsModule } from "./payments/module";
import { aiModule } from "./ai/module";
import { smsModule } from "./sms/module";
import { featureFlagsModule } from "./feature_flags/module";
import { serverModule } from "./server/module";
import { searchModule } from "./search/module";
import { outboxModule } from "./outbox/module";
import { auditModule } from "./audit/module";
import { impersonationModule } from "./impersonation/module";

/**
 * Declaration order inside a group is nav order. Groups themselves are ordered
 * by NAV_GROUPS.
 */
export const modules: AdminModule[] = [
  overviewModule,

  // Customers — who is on the platform. The support desk lives here.
  usersModule,
  accountsModule,
  signupsModule,

  // Revenue — subscriptions, dunning, and the catalogue they bill against.
  revenueModule,
  paymentsModule,

  // Platform services — the metered things we run for tenants.
  aiModule,
  smsModule,
  featureFlagsModule,

  // Operations — is the platform healthy, and who changed what.
  serverModule,
  impersonationModule,
  searchModule,
  outboxModule,
  auditModule,
];

export { NAV_GROUPS };
export type { NavGroup };

/** Routes the session is allowed to see, in declaration order. */
export function visibleRoutes(m: AdminModule, session: AdminSession | null): ModuleRoute[] {
  return m.routes.filter(
    (r) => !r.requiredPermission || hasPermission(session, r.requiredPermission),
  );
}

/** Sub-nav entries: visible, labelled, and not a dynamic-segment detail route. */
export function navRoutes(m: AdminModule, session: AdminSession | null): ModuleRoute[] {
  return visibleRoutes(m, session).filter((r) => r.label && !r.path.startsWith(":"));
}

/**
 * A module is in the nav when the session holds its module-level permission
 * *and* at least one of its routes. The second half matters for Revenue: a
 * finance viewer with only `plans.manage` should still see the module, landing
 * on Plans rather than on a Billing overview they cannot read.
 */
export function isModuleVisible(m: AdminModule, session: AdminSession | null): boolean {
  if (m.requiredPermission && !hasPermission(session, m.requiredPermission)) return false;
  return visibleRoutes(m, session).length > 0;
}

export function modulesForNav(session: AdminSession | null): AdminModule[] {
  return modules.filter((m) => isModuleVisible(m, session));
}

export function modulesInGroup(group: NavGroup, session: AdminSession | null): AdminModule[] {
  return modulesForNav(session).filter((m) => m.group === group);
}

/**
 * Where a sidebar click lands. Modules with a single empty-path route land on
 * `/{key}`; the rest land on their first visible child, skipping dynamic
 * segments like ":id" which cannot be a landing page.
 */
export function firstPathOf(m: AdminModule, session: AdminSession | null = null): string {
  const routes = session ? visibleRoutes(m, session) : m.routes;
  const first = routes.find((r) => r.path !== "" && !r.path.startsWith(":"));
  return first?.path ?? "";
}

export function landingHrefOf(m: AdminModule, session: AdminSession | null = null): string {
  const p = firstPathOf(m, session);
  return p ? `/${m.key}/${p}` : `/${m.key}`;
}

/**
 * Old URLs kept alive.
 *
 * The regroup moved four modules and merged two. These are internal bookmarks,
 * Slack links and runbook steps that would otherwise 404 with no explanation,
 * so every path the portal has ever served still resolves. A `*` suffix
 * forwards the remainder of the path.
 */
export const LEGACY_REDIRECTS: Record<string, string> = {
  "/dashboard": "/overview",

  // Billing + Pricing merged into Revenue.
  "/billing": "/revenue/overview",
  "/billing/overview": "/revenue/overview",
  "/billing/subscriptions": "/revenue/subscriptions",
  "/billing/dunning": "/revenue/dunning",
  "/pricing": "/revenue/plans",
  "/pricing/plans": "/revenue/plans",
  "/pricing/coupons": "/revenue/coupons",

  // "Infra" only ever meant OpenSearch; it is now "Search indexing".
  "/infra": "/search/indices",
  "/infra/opensearch/indices": "/search/indices",
  "/infra/opensearch/jobs": "/search/jobs",

  // Underscore key predated the rest of the app's kebab-case routes.
  "/feature_flags": "/feature-flags",
};
